/**
 * Ein Gespräch im Nachrichtenfenster: Blasen, Zuordnung, Antworten.
 *
 * Kunde links, wir rechts. Zitierter Verlauf und lange Signaturen schneidet der
 * Server ab („ganze Mail" zeigt sie). Die Antwort geht aus dem eigenen Postfach
 * im selben Mailverlauf hinaus — der Kunde merkt keinen Unterschied zu Gmail.
 * Zuordnen, Bestätigen und Ausblenden gelten wie am Projekt für das ganze Gespräch.
 *
 * „✂ Ab einer Nachricht" (25.09.2026, Migration 176): Benjamin sieht beim Lesen,
 * dass es ab einer Mail um ein neues Angebot, also ein anderes Projekt geht.
 * Dann tippt er diese Mail an, wählt das Projekt — und alles ab dort, was bisher
 * zum selben Projekt gehörte, wandert mit, über alle Gespräche des Chats (eine
 * Mail mit neuem Betreff ist ein eigenes Gespräch). Mails anderer Projekte
 * bleiben, wo sie sind. Dieselbe Geste mit dem alten Projekt hebt es wieder auf.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { fehlerText } from "@vimaana/ui";

import { useMailKonto } from "../../hooks/useMail";
import { useFaden, useGelesen, type Blase, type Faden, type Gespraech } from "../../hooks/useNachrichten";
import {
  anhangUrl, useLeedKandidaten, useMailSignatur, usePostAbHier, usePostAusblenden, usePostBestaetigen, usePostSchreiben, usePostUmhaengen,
} from "../../hooks/usePost";
import { ART_LABEL, reBetreff, tagTitel, wirName, zeitKurz } from "../../lib/nachrichten";
import { vorname } from "../../lib/notizen";
import Combobox from "../Combobox";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import KundeBlatt from "../kunden/KundeBlatt";
import PostSchreiben from "../projekt/PostSchreiben";
import ProjektNeuDialog from "../projekt/ProjektNeuDialog";
import BesichtigungPlanen from "./BesichtigungPlanen";
import LeedUebernehmen from "./LeedUebernehmen";

/** `sichtbar = false`: das Fenster ist geschlossen, bleibt aber stehen — dann nichts als gelesen markieren.
 *  `onWechseln`: zu einem anderen Chat springen (aus dem Leed wurde ein Kunde). */
type Props = { schluessel: string; onZurueck: () => void; onSchliessen: () => void; sichtbar?: boolean; onWechseln?: (schluessel: string) => void };
/** Text, der ins Antwortfeld eines bestimmten Chats gehört (Terminbestätigung). */
type Vorlage = { schluessel: string; text: string; nr: number };

export default function FadenAnsicht({ schluessel, onZurueck, onSchliessen, sichtbar = true, onWechseln }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: f, isLoading, error } = useFaden(schluessel, sichtbar);
  const gelesen = useGelesen();
  const zuordnen = usePostUmhaengen();
  const konto = useMailKonto().data;
  const sig = useMailSignatur().data;
  const [menue, setMenue] = useState(false);
  const [kundeOffen, setKundeOffen] = useState(false);
  const [leedOffen, setLeedOffen] = useState(false);
  const [projektNeu, setProjektNeu] = useState(false);
  const [besichtigung, setBesichtigung] = useState(false);
  const [vorlage, setVorlage] = useState<Vorlage | null>(null);
  const [schreiben, setSchreiben] = useState<"" | "antwort" | "neu">("");
  const [vorschau, setVorschau] = useState<Dokument>(null);
  const [aktivKey, setAktivKey] = useState("");
  // Schneiden: `teilen` = Auswahlmodus, `abId` = die Nachricht, ab der es ein anderes Projekt ist.
  const [teilen, setTeilen] = useState(false);
  const [abId, setAbId] = useState("");
  const [projektNeuAb, setProjektNeuAb] = useState(false);
  const abHier = usePostAbHier();
  const verlauf = useRef<HTMLDivElement>(null);

  const namen = useMemo(() => {
    const n: Record<string, string> = {};
    for (const p of sig?.partner || []) n[p.email.toLowerCase()] = vorname(p.name) || p.email;
    if (sig?.email) n[sig.email.toLowerCase()] = "Du";
    return n;
  }, [sig]);

  useEffect(() => { setAktivKey(""); setTeilen(false); setAbId(""); }, [schluessel]);

  // Was neu war, ist mit dem Öffnen gelesen — auch wenn später etwas nachkommt.
  const neueGespraeche = (f?.gespraeche || []).filter((g) => g.neu > 0).map((g) => g.schluessel);
  const letzteId = f?.nachrichten[f.nachrichten.length - 1]?.id;
  useEffect(() => {
    if (sichtbar && neueGespraeche.length && !gelesen.isPending) gelesen.mutate(neueGespraeche);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel, neueGespraeche.join("|"), sichtbar]);

  useEffect(() => {
    const el = verlauf.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [letzteId]);

  const neuLaden = () => qc.invalidateQueries({ queryKey: ["nachrichten"] });

  if (isLoading || !f) {
    return (
      <>
        <FadenKopf titel="" onZurueck={onZurueck} onSchliessen={onSchliessen} />
        <p className="nb-leer">{error ? fehlerText(error) : "Lade …"}</p>
      </>
    );
  }

  // Geantwortet wird im gewählten Gespräch — ohne Wahl im neuesten.
  const aktiv: Gespraech | null = f.gespraeche.find((g) => g.schluessel === aktivKey) || f.gespraeche[f.gespraeche.length - 1] || null;
  const mehrere = f.gespraeche.length > 1;
  const kamAnAndere = !!aktiv?.postfach && !!konto?.email && aktiv.postfach.toLowerCase() !== konto.email.toLowerCase();
  const gespraechVon = (key: string) => f.gespraeche.find((g) => g.schluessel === key);
  // Die gewählte Nachricht und alles Spätere, das bisher im selben Projekt stand —
  // über alle Gespräche des Chats.
  const ab = abId ? f.nachrichten.find((n) => n.id === abId) : undefined;
  const projektVon = (key: string) => gespraechVon(key)?.projekt_id || "";
  const abProjekt = ab ? projektVon(ab.gespraech) : "";
  const imSchnitt = (n: Blase) => !!ab && n.zeitpunkt >= ab.zeitpunkt && projektVon(n.gespraech) === abProjekt;
  const schnitt = ab ? f.nachrichten.filter(imSchnitt) : [];
  const schnittZahl = schnitt.length;
  const schnittGespraeche = new Set(schnitt.map((n) => n.gespraech)).size;
  const teilenEnde = () => { setTeilen(false); setAbId(""); abHier.reset(); };
  /** Zum verknüpften Projekt springen — das Fenster schließt, behält aber seinen Stand. */
  const zumProjekt = (projektId: string) => { onSchliessen(); navigate(`/projekte/${projektId}`); };
  const schneiden = (projektId: string) => {
    if (!ab) return;
    // Je Gespräch die erste betroffene Nachricht — dort schneidet der Server.
    const erste = new Map<string, string>();
    for (const n of schnitt) if (!erste.has(n.gespraech)) erste.set(n.gespraech, n.id);
    abHier.mutate({ messageIds: Array.from(erste.values()), projektId }, { onSuccess: () => { teilenEnde(); setAktivKey(""); neuLaden(); } });
  };

  return (
    <>
      <FadenKopf
        titel={f.titel} unter={mehrere ? `${f.gespraeche.length} Gespräche` : aktiv?.betreff || ""} art={f.art}
        onZurueck={onZurueck} onSchliessen={onSchliessen}
        onTitel={f.kunde_id ? () => setKundeOffen(true) : undefined}
        menue={menue} setMenue={setMenue}
      >
        <button type="button" role="menuitem" onClick={() => { setMenue(false); setSchreiben("neu"); }}>Neue Mail (neuer Betreff)</button>
        {f.kunde_id && <button type="button" role="menuitem" onClick={() => { setMenue(false); setKundeOffen(true); }}>Kundendaten</button>}
        {(f.art === "leed" ? !!f.leed : !!f.kunde_id) && (
          <button type="button" role="menuitem" onClick={() => { setMenue(false); setBesichtigung(true); }}>Besichtigung planen</button>
        )}
        {f.art !== "leed" && f.nachrichten.length > 1 && (
          <button type="button" role="menuitem" onClick={() => { setMenue(false); setTeilen(true); }}>✂ Ab einer Nachricht anderes Projekt</button>
        )}
        {aktiv?.projekt_id && (
          <button type="button" role="menuitem" onClick={() => { setMenue(false); zumProjekt(aktiv.projekt_id); }}>
            Zum Projekt
          </button>
        )}
        {aktiv && <Ausblenden messageId={aktiv.antwort_auf} mehrere={mehrere} onFertig={() => { setMenue(false); neuLaden(); if (!mehrere) onZurueck(); }} />}
      </FadenKopf>

      {teilen ? (
        ab ? (
          <SchnittWahl f={f} ab={ab} zahl={schnittZahl} gespraeche={schnittGespraeche} projektVorher={abProjekt}
            laeuft={abHier.isPending} fehler={abHier.error} onWahl={schneiden}
            onNeuesProjekt={f.art === "kunde" && f.kunde_id ? () => setProjektNeuAb(true) : undefined} onAbbrechen={teilenEnde} />
        ) : (
          <div className="nb-zuordnung ist-teilen">
            <span>Tippe die Nachricht an, ab der es um ein anderes Projekt geht.</span>
            <button type="button" className="vg-btn" onClick={teilenEnde}>Abbrechen</button>
          </div>
        )
      ) : (f.art === "leed" || aktiv) && (
        <Zuordnung f={f} aktiv={aktiv} mehrere={mehrere} onNeuesProjekt={() => setProjektNeu(true)} onLeed={() => setLeedOffen(true)} onFertig={neuLaden}
          onBesichtigung={() => setBesichtigung(true)} onProjekt={zumProjekt}
          onTeilen={f.nachrichten.length > 1 ? () => setTeilen(true) : undefined} />
      )}

      <div className={"nb-verlauf" + (teilen ? " ist-teilen" : "")} ref={verlauf}>
        {f.nachrichten.length === 0 && <p className="nb-leer">Noch keine Mails mit {f.titel}.</p>}
        {f.nachrichten.map((n, i) => {
          const vorher = f.nachrichten[i - 1];
          const neuerTag = !vorher || new Date(vorher.zeitpunkt).toDateString() !== new Date(n.zeitpunkt).toDateString();
          const g = mehrere && (!vorher || vorher.gespraech !== n.gespraech) ? gespraechVon(n.gespraech) : undefined;
          return (
            <Fragment key={n.id}>
              {neuerTag && <p className="nb-tag">{tagTitel(n.zeitpunkt)}</p>}
              {g && (
                <button
                  type="button" className={"nb-gespraech" + (g.schluessel === aktiv?.schluessel ? " ist-aktiv" : "")}
                  onClick={() => setAktivKey(g.schluessel)} aria-pressed={g.schluessel === aktiv?.schluessel}
                  title="In diesem Gespräch antworten und zuordnen"
                >
                  <span className="nb-gespraech-betreff">{g.betreff || "ohne Betreff"}</span>
                  <span className="nb-gespraech-projekt">{g.projekt_titel ? (g.vorschlag ? `${g.projekt_titel}?` : g.projekt_titel) : "ohne Projekt"}</span>
                </button>
              )}
              {teilen && n.id === abId && <p className="nb-schnitt" aria-hidden="true">✂ ab hier</p>}
              <BlaseAnsicht n={n} wer={n.richtung === "out" ? wirName(n.von, namen) : ""} onVorschau={setVorschau}
                waehlen={teilen ? () => setAbId(n.id === abId ? "" : n.id) : undefined}
                markiert={teilen && imSchnitt(n)} gedimmt={teilen && !!ab && !imSchnitt(n)} />
            </Fragment>
          );
        })}
      </div>

      {teilen ? (
        <p className="nb-antwort nb-antwort-hinweis">
          {ab ? "Markiert: diese Nachricht und alles danach, was bisher im selben Projekt stand. Eine andere antippen, um den Punkt zu verschieben."
            : "Die gewählte Nachricht und alles danach, was bisher im selben Projekt stand, wandern ins andere Projekt."}
        </p>
      ) : (
        <Antwort f={f} aktiv={aktiv} mehrere={mehrere} kamAnAndere={kamAnAndere} namen={namen} verbunden={konto?.verbunden !== false}
          onAusfuehrlich={() => setSchreiben(aktiv ? "antwort" : "neu")} onGesendet={() => { setVorlage(null); neuLaden(); }}
          vorlage={vorlage && vorlage.schluessel === f.schluessel ? vorlage : null} />
      )}

      <DokumentLightbox dok={vorschau} onClose={() => setVorschau(null)} />
      {schreiben && (
        <PostSchreiben
          onClose={() => { setSchreiben(""); neuLaden(); }}
          projektId={(schreiben === "antwort" ? aktiv?.projekt_id : "") || undefined} kundeId={f.kunde_id || undefined}
          anfrageId={f.anfrage_id || undefined} lieferantId={f.lieferant_id || undefined}
          ziele={(aktiv?.an || f.an) ? [{ email: aktiv?.an || f.an, wer: f.titel }] : []} an={aktiv?.an || f.an}
          betreff={schreiben === "antwort" && aktiv ? reBetreff(aktiv.betreff) : ""}
          antwortAuf={schreiben === "antwort" ? aktiv?.antwort_auf : undefined} titel={f.titel}
        />
      )}
      {kundeOffen && f.kunde_id && (
        <div className="nb-oben-rahmen"><KundeBlatt kundeId={f.kunde_id} onClose={() => setKundeOffen(false)} /></div>
      )}
      {leedOffen && f.leed && (
        <LeedUebernehmen leed={f.leed} faden={f} vorschlagTitel={aktiv?.betreff || ""} onClose={() => setLeedOffen(false)} onFertig={() => { setLeedOffen(false); neuLaden(); onZurueck(); }} />
      )}
      {besichtigung && (
        <BesichtigungPlanen
          f={f} onClose={() => setBesichtigung(false)}
          onFertig={(e) => {
            setBesichtigung(false);
            const ziel = f.art === "leed" ? `kunde:${e.kundeId}` : f.schluessel;
            if (e.bestaetigung) setVorlage({ schluessel: ziel, text: e.bestaetigung, nr: Date.now() });
            neuLaden();
            if (ziel !== f.schluessel) onWechseln?.(ziel);
          }}
          onPlaner={(datum) => { onSchliessen(); navigate(`/planung?datum=${datum}`); }}
        />
      )}
      {projektNeuAb && ab && (
        <ProjektNeuDialog
          kundeId={f.kunde_id} vorschlagTitel={ab.betreff}
          hinweis="der Verlauf ab der markierten Nachricht wird gleich diesem Projekt zugeordnet"
          onSchliessen={() => setProjektNeuAb(false)}
          onAngelegt={(p) => { setProjektNeuAb(false); schneiden(p.id); }}
        />
      )}
      {projektNeu && aktiv && (
        <ProjektNeuDialog
          kundeId={f.kunde_id} vorschlagTitel={aktiv.betreff}
          hinweis="das Gespräch wird gleich diesem Projekt zugeordnet"
          onSchliessen={() => setProjektNeu(false)}
          onAngelegt={(p) => {
            setProjektNeu(false);
            zuordnen.mutate({ messageId: aktiv.antwort_auf, projektId: p.id }, { onSuccess: neuLaden });
          }}
        />
      )}
    </>
  );
}

function FadenKopf({ titel, unter, art, onZurueck, onSchliessen, onTitel, menue, setMenue, children }: {
  titel: string; unter?: string; art?: Faden["art"]; onZurueck: () => void; onSchliessen: () => void;
  onTitel?: () => void; menue?: boolean; setMenue?: (v: boolean) => void; children?: ReactNode;
}) {
  return (
    <header className="nb-kopf ist-faden">
      <button type="button" className="nb-zurueck" aria-label="Zurück zur Liste" onClick={onZurueck}>‹</button>
      <div className="nb-kopf-text">
        {onTitel ? (
          <button type="button" className="nb-kopf-name" onClick={onTitel} title="Kundendaten zeigen">{titel}</button>
        ) : (
          <span className="nb-kopf-name">{titel}</span>
        )}
        <span className="nb-kopf-unter">
          {art && art !== "kunde" && <span className={`nb-art ist-${art}`}>{ART_LABEL[art]}</span>} {unter}
        </span>
      </div>
      {setMenue && (
        <div className="nb-menue-rahmen">
          <button type="button" className="nb-zu" aria-label="Mehr" aria-haspopup="menu" aria-expanded={!!menue} onClick={() => setMenue(!menue)}>⋯</button>
          {menue && <div className="nb-menue" role="menu">{children}</div>}
        </div>
      )}
      <button type="button" className="nb-zu" aria-label="Schließen" onClick={onSchliessen}>×</button>
    </header>
  );
}

function Ausblenden({ messageId, mehrere, onFertig }: { messageId: string; mehrere: boolean; onFertig: () => void }) {
  const ausblenden = usePostAusblenden();
  return (
    <button
      type="button" role="menuitem" disabled={ausblenden.isPending}
      onClick={() => ausblenden.mutate({ messageId, gespraech: true, ausblenden: true }, { onSuccess: onFertig })}
    >
      {mehrere ? "Dieses Gespräch ausblenden" : "Gespräch ausblenden"}
    </button>
  );
}

/** Unten statt der Antwort, sobald eine Nachricht markiert ist: wohin der Rest gehört. */
function SchnittWahl({ f, ab, zahl, gespraeche, projektVorher, laeuft, fehler, onWahl, onNeuesProjekt, onAbbrechen }: {
  f: Faden; ab: Blase; zahl: number; gespraeche: number; projektVorher: string; laeuft: boolean; fehler: unknown;
  onWahl: (projektId: string) => void; onNeuesProjekt?: () => void; onAbbrechen: () => void;
}) {
  const [ziel, setZiel] = useState("");
  const eintraege = f.projekte.filter((p) => !p.erledigt && p.id !== projektVorher).map((p) => ({
    id: p.id, label: p.titel || "Projekt", zusatz: [p.zusatz, p.grund].filter(Boolean).join(" · "),
  }));
  const wann = new Date(ab.zeitpunkt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="nb-zuordnung ist-teilen nb-schnitt-wahl">
      <span className="nb-zuordnung-titel">
        ✂ Ab {wann} · {zahl} {zahl === 1 ? "Nachricht" : "Nachrichten"}{gespraeche > 1 ? ` aus ${gespraeche} Gesprächen` : ""}
        {projektVorher ? ` (bisher ${f.projekte.find((p) => p.id === projektVorher)?.titel || "anderes Projekt"})` : ""} nach
      </span>
      <div className="nb-schnitt-zeile">
        <div className="nb-schnitt-combo">
          <Combobox
            wert={ziel} eintraege={eintraege} onWaehlen={setZiel}
            leerLabel={f.art === "lieferant" ? "Kein Projekt" : "Kein Projekt — bleibt beim Kunden"}
            platzhalter="Projekt wählen" ariaLabel="Projekt ab dieser Nachricht" disabled={laeuft}
          />
        </div>
        <button type="button" className="vg-btn vg-btn-primary" disabled={laeuft || !ziel} onClick={() => onWahl(ziel)}>
          {laeuft ? "…" : "Zuordnen"}
        </button>
      </div>
      <div className="nb-schnitt-fuss nb-zuordnung-knoepfe">
        {onNeuesProjekt && <button type="button" className="vg-btn" onClick={onNeuesProjekt}>Neues Projekt</button>}
        <button type="button" className="vg-btn" onClick={onAbbrechen}>Abbrechen</button>
      </div>
      {!!fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}
    </div>
  );
}

function Zuordnung({ f, aktiv, mehrere, onNeuesProjekt, onLeed, onFertig, onTeilen, onBesichtigung, onProjekt }: {
  f: Faden; aktiv: Gespraech | null; mehrere: boolean; onNeuesProjekt: () => void; onLeed: () => void; onFertig: () => void;
  onTeilen?: () => void; onBesichtigung: () => void; onProjekt: (projektId: string) => void;
}) {
  const umhaengen = usePostUmhaengen();
  const bestaetigen = usePostBestaetigen();
  // Vorher stand hier immer „noch kein Kunde" — auch wenn die Kundin längst im Stamm war (26.10.2026).
  const kandidaten = useLeedKandidaten(f.art === "leed" ? f.leed?.id : undefined).data ?? [];
  const fehler = umhaengen.error || bestaetigen.error;

  if (f.art === "leed") {
    const k = kandidaten[0];
    return (
      <div className="nb-zuordnung ist-leed">
        <span>
          {k ? `Neue Anfrage — vermutlich schon im Kundenstamm: ${k.name}${k.anschrift ? `, ${k.anschrift}` : ""}${kandidaten.length > 1 ? ` (+${kandidaten.length - 1})` : ""}.`
            : "Neue Anfrage — im Kundenstamm nicht gefunden."}
        </span>
        <span className="nb-zuordnung-knoepfe">
          <button type="button" className="vg-btn" onClick={onBesichtigung} disabled={!f.leed}>Besichtigung planen</button>
          <button type="button" className="vg-btn vg-btn-primary" onClick={onLeed} disabled={!f.leed}>{k ? "Kunde zuordnen" : "Als Kunde übernehmen"}</button>
        </span>
      </div>
    );
  }
  if (!aktiv) return null;
  const eintraege = f.projekte.filter((p) => !p.erledigt || p.id === aktiv.projekt_id).map((p) => ({
    id: p.id, label: p.titel || "Projekt", zusatz: [p.zusatz, p.grund].filter(Boolean).join(" · "),
  }));
  const setzen = (projektId: string) => {
    if (projektId === aktiv.projekt_id) return;
    umhaengen.mutate({ messageId: aktiv.antwort_auf, projektId }, { onSuccess: onFertig });
  };
  return (
    <div className={"nb-zuordnung" + (aktiv.projekt_id ? "" : " ist-offen")}>
      <span className="nb-zuordnung-titel">
        Projekt{mehrere && <small className="nb-zuordnung-fuer"> für „{aktiv.betreff || "ohne Betreff"}"</small>}
      </span>
      <div className="nb-zuordnung-wahl">
        <Combobox
          key={aktiv.schluessel}
          wert={aktiv.projekt_id} eintraege={eintraege} onWaehlen={setzen}
          leerLabel={f.art === "lieferant" ? "Kein Projekt" : "Kein Projekt — bleibt beim Kunden"}
          platzhalter={f.art === "lieferant" ? "Welches Projekt? Tippen zum Suchen" : "Projekt wählen"}
          ariaLabel="Projekt für dieses Gespräch" disabled={umhaengen.isPending}
        />
      </div>
      {aktiv.projekt_id && aktiv.vorschlag && (
        <button type="button" className="vg-btn vg-btn-primary" disabled={bestaetigen.isPending}
          onClick={() => bestaetigen.mutate(aktiv.antwort_auf, { onSuccess: onFertig })}>
          Stimmt
        </button>
      )}
      {/* Direkt zum verknüpften Projekt (Benjamin, 13.10.2026) — nicht erst über „⋯". */}
      {aktiv.projekt_id && (
        <button type="button" className="vg-btn nb-zum-projekt" onClick={() => onProjekt(aktiv.projekt_id)}
          title="Projekt öffnen" aria-label="Zum Projekt">
          Zum Projekt <span aria-hidden="true">→</span>
        </button>
      )}
      {((f.art === "kunde" && f.kunde_id) || onTeilen) && (
        <span className="nb-zuordnung-knoepfe">
          {f.art === "kunde" && f.kunde_id && (
            <button type="button" className="vg-btn" onClick={onBesichtigung}>Besichtigung planen</button>
          )}
          {f.art === "kunde" && f.kunde_id && (
            <button type="button" className="vg-btn" onClick={onNeuesProjekt}>Neues Projekt</button>
          )}
          {onTeilen && (
            <button type="button" className="vg-btn" onClick={onTeilen} title="Ab einer bestimmten Nachricht gehört der Verlauf zu einem anderen Projekt">
              Ab Nachricht trennen
            </button>
          )}
        </span>
      )}
      {fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}
    </div>
  );
}

function groesse(bytes: number): string {
  if (!bytes) return "";
  return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function BlaseAnsicht({ n, wer, onVorschau, waehlen, markiert, gedimmt }: {
  n: Blase; wer: string; onVorschau: (d: Dokument) => void;
  /** Schneiden: die Blase ist antippbar (Migration 176). */
  waehlen?: () => void; markiert?: boolean; gedimmt?: boolean;
}) {
  const [voll, setVoll] = useState(false);
  const aus = n.richtung === "out";
  return (
    <div
      className={"nb-blase-zeile" + (aus ? " ist-aus" : "") + (waehlen ? " ist-waehlbar" : "") + (markiert ? " ist-markiert" : "") + (gedimmt ? " ist-gedimmt" : "")}
      onClickCapture={waehlen ? (e) => { e.preventDefault(); e.stopPropagation(); waehlen(); } : undefined}
      role={waehlen ? "button" : undefined} tabIndex={waehlen ? 0 : undefined}
      aria-pressed={waehlen ? !!markiert : undefined}
      onKeyDown={waehlen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); waehlen(); } } : undefined}
    >
      <div className={"nb-blase" + (aus ? " ist-aus" : "") + (n.leise ? " ist-leise" : "") + (n.neu ? " ist-neu" : "")}>
        {aus && <span className="nb-blase-wer">{wer}</span>}
        {n.leise && <span className="nb-art ist-lieferant">{n.leise}</span>}
        <p className="nb-blase-text">{voll && n.voll ? n.voll : n.text || n.betreff || "—"}</p>
        {n.voll && (
          <button type="button" className="nb-voll" onClick={() => setVoll(!voll)}>{voll ? "gekürzt zeigen" : "ganze Mail"}</button>
        )}
        {n.anhaenge.length > 0 && (
          <ul className="nb-anhaenge" aria-label="Anhänge">
            {n.anhaenge.map((a) => (
              <li key={a.nr}>
                {a.mime === "application/pdf" || (a.mime.startsWith("image/") && !a.mime.includes("svg")) ? (
                  <button type="button" className="nb-anhang" onClick={() => onVorschau({
                    titel: a.name, unterzeile: zeitKurz(n.zeitpunkt), url: anhangUrl(n.id, a.nr), bild: a.mime.startsWith("image/"),
                    seitenZahlUrl: `${anhangUrl(n.id, a.nr)}/seiten`, seiteUrl: (i) => `${anhangUrl(n.id, a.nr)}/seite/${i}`,
                  })}>
                    <span aria-hidden="true">{a.mime === "application/pdf" ? "📄" : "🖼"}</span> {a.name} <small>{groesse(a.bytes)}</small>
                  </button>
                ) : (
                  <a className="nb-anhang" href={anhangUrl(n.id, a.nr)} target="_blank" rel="noopener noreferrer">
                    <span aria-hidden="true">📎</span> {a.name} <small>{groesse(a.bytes)}</small>
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
        <time className="nb-blase-zeit" dateTime={n.zeitpunkt}>
          {new Date(n.zeitpunkt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
        </time>
      </div>
    </div>
  );
}

function Antwort({ f, aktiv, mehrere, kamAnAndere, namen, verbunden, onAusfuehrlich, onGesendet, vorlage }: {
  f: Faden; aktiv: Gespraech | null; mehrere: boolean; kamAnAndere: boolean; namen: Record<string, string>; verbunden: boolean;
  onAusfuehrlich: () => void; onGesendet: () => void; vorlage: Vorlage | null;
}) {
  const [text, setText] = useState("");
  const senden = usePostSchreiben();
  const feld = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setText(""); senden.reset(); /* anderer Chat, leeres Feld */ }, [f.schluessel]); // eslint-disable-line react-hooks/exhaustive-deps
  // Eine vorbereitete Antwort (Terminbestätigung) — nach dem Leeren, damit sie stehen bleibt.
  useEffect(() => {
    if (!vorlage) return;
    setText(vorlage.text);
    feld.current?.focus();
  }, [vorlage?.nr, f.schluessel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = feld.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  if (!verbunden) {
    return <p className="nb-antwort-hinweis nb-antwort">Zum Antworten muss dein Postfach verbunden sein — das geht in den Einstellungen.</p>;
  }
  if (!aktiv) {
    // Noch keine Post: die erste Mail braucht einen Betreff — das große Schreibfenster.
    return (
      <div className="nb-antwort">
        <button type="button" className="vg-btn vg-btn-primary" onClick={onAusfuehrlich}>Mail an {f.titel} schreiben</button>
      </div>
    );
  }
  const an = aktiv.an || f.an;
  const bereit = !!an && text.trim().length > 0 && !senden.isPending;
  const los = () => {
    if (!bereit) return;
    senden.mutate({
      an, betreff: reBetreff(aktiv.betreff), text: text.trim(),
      projekt_id: aktiv.projekt_id, kunde_id: f.kunde_id, anfrage_id: f.anfrage_id, lieferant_firma_id: f.lieferant_id,
      antwort_auf: aktiv.antwort_auf,
    }, { onSuccess: () => { setText(""); onGesendet(); } });
  };
  const empfaenger = namen[aktiv.postfach.toLowerCase()];
  return (
    <div className="nb-antwort">
      {mehrere && <p className="nb-antwort-hinweis">Antwort auf „{aktiv.betreff || "ohne Betreff"}" — anderes Gespräch oben im Verlauf antippen.</p>}
      {kamAnAndere && empfaenger && <p className="nb-antwort-hinweis">Kam an {empfaenger} — du antwortest aus deinem Postfach.</p>}
      {!an && <p className="nb-antwort-hinweis">Keine Adresse zum Antworten gefunden — „⤢" öffnet das Schreibfenster.</p>}
      {senden.error && <p className="vg-post-fehler">{fehlerText(senden.error)}</p>}
      <div className="nb-antwort-zeile">
        <textarea
          ref={feld} className="nb-antwort-feld" rows={1} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); los(); } }}
          placeholder="Antworten …" aria-label="Antwort" disabled={!an}
        />
        <button type="button" className="nb-ausfuehrlich" onClick={onAusfuehrlich} title="Ausführlich schreiben — mit Kopie und Clara" aria-label="Ausführlich schreiben">⤢</button>
        <button type="button" className="vg-btn vg-btn-primary nb-senden" disabled={!bereit} onClick={los}>
          {senden.isPending ? "…" : "Senden"}
        </button>
      </div>
    </div>
  );
}
